
// ================= IMPORTS =================
import React, {
  useEffect,
  useState,
  useContext,
} from "react";

import API from "../api";

import {
  useParams,
  useNavigate,
  Link,
} from "react-router-dom";

import { AuthContext } from "../context/AuthContext";

import {
  motion,
  AnimatePresence,
} from "framer-motion";

import {
  ArrowLeft,
  Edit3,
  Trash2,
  Save,
  X,
  Building2,
  Calendar,
  DollarSign,
  Globe,
  Loader2,
  Mail,
  Phone,
  Briefcase,
} from "lucide-react";

// ================= CLIENT TYPES =================
const clientTypes = [
  { label: "Lead", value: "lead" },
  { label: "Contact", value: "contact" },
  { label: "Client", value: "client" },
];

// ================= SERVICES =================
const services = [
  "Web Development",
  "Mobile App Development",
  "UI/UX Design",
  "SEO",
  "Digital Marketing",
  "Software Development",
  "Cloud Computing",
  "AI & Machine Learning",
  "Data Analytics",
  "Cybersecurity",
  "DevOps Services",
  "E-commerce Development",
  "CRM Development",
  "ERP Solutions",
  "Blockchain Development",
  "IT Consulting",
  "Business Email",
];

// ================= SALES JOURNEY =================
const salesJourneySteps = [
  {
    star: 1,
    title: "Lead Added",
    color: "bg-blue-500",
  },
  {
    star: 2,
    title: "Intro Sent",
    color: "bg-blue-500",
  },
  {
    star: 3,
    title: "Follow-Up",
    color: "bg-blue-500",
  },
  {
    star: 4,
    title: "Connected",
    color: "bg-blue-500",
  },
  {
    star: 5,
    title: "Qualified",
    color: "bg-orange-500",
  },
  {
    star: 6,
    title: "Demo Done",
    color: "bg-orange-500",
  },
  {
    star: 7,
    title: "Requirements Logged",
    color: "bg-orange-500",
  },
  {
    star: 8,
    title: "Proposal Shared",
    color: "bg-green-500",
  },
  {
    star: 9,
    title: "Negotiation",
    color: "bg-green-500",
  },
  {
    star: 10,
    title: "Closed Won",
    color: "bg-yellow-500",
  },
];

// ================= MAIN COMPONENT =================
const ClientDetails = () => {
  const { id } = useParams();

  const navigate = useNavigate();

  const {
  user,
  role,
  orgSlug,
  loading,
} = useContext(AuthContext);

  const [client, setClient] =
    useState(null);

  const [editMode, setEditMode] =
    useState(false);

  const [formData, setFormData] =
    useState({});

  const [pageLoading, setPageLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [loadingDelete, setLoadingDelete] =
    useState(false);

  // ================= FETCH =================
  useEffect(() => {
    if (
      loading ||
      !user ||
      !orgSlug
    )
      return;

    fetchClient();
  }, [id, user, loading, orgSlug]);

  // ================= GET CLIENT =================
  const fetchClient = async () => {
    try {
      setPageLoading(true);

      const res = await API.get(
        `clients/${id}/`
      );

      setClient(res.data);

      setFormData(res.data);
    } catch (error) {
      console.error(error);

      alert(
        "Failed to load client details"
      );
    } finally {
      setPageLoading(false);
    }
  };

  // ================= INPUT CHANGE =================
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]:
        e.target.value,
    });
  };

  // ================= PHONE VALIDATION =================
  const validatePhone = (phone) =>
    /^[6-9]\d{9}$/.test(phone);

  // ================= UPDATE CLIENT =================
  const handleUpdate = async () => {
    if (
      formData.phone &&
      !validatePhone(
        formData.phone
      )
    ) {
      return alert(
        "Enter valid 10 digit phone number"
      );
    }

    try {
      setSaving(true);

      await API.patch(
        `clients/${id}/update/`,
        {
          ...formData,

          revenue:
            formData.revenue
              ? parseFloat(
                  formData.revenue
                )
              : 0,
        }
      );

      setEditMode(false);

      await fetchClient();

      alert(
        "Client updated successfully!"
      );
    } catch (error) {
      console.error(error);

      alert(
        error?.response?.data
          ?.error ||
          "Update failed"
      );
    } finally {
      setSaving(false);
    }
  };

  // ================= DELETE CLIENT =================
  const handleDelete = async () => {
    const confirmDelete =
      window.confirm(
        "Are you sure? This action cannot be undone."
      );

    if (!confirmDelete) return;

    try {
      setLoadingDelete(true);

      await API.delete(
        `clients/${id}/delete/`
      );

      alert(
        "Client deleted successfully"
      );

      navigate(
        `/${orgSlug}/clients`
      );
    } catch (error) {
      console.error(error);

      alert("Delete failed");
    } finally {
      setLoadingDelete(false);
    }
  };

  // ================= UPDATE SALES STAGE =================
  const updateSalesStage = async (step) => {
  try {

    // 🔒 ADMIN LOCK
    if (
      step.star >= 8 &&
      role !== "admin"
    ) {
      return alert(
        "Only admin can complete this stage"
      );
    }

    // ✅ USE EXISTING UPDATE API
    await API.patch(
      `clients/${id}/update/`,
      {
        salesStar: step.star,
        salesStage: step.title,
      }
    );

    // ✅ REFRESH CLIENT
    fetchClient();

  } catch (error) {

    console.error(error);

    alert("Failed to update stage");
  }
};

  // ================= LOADING =================
  if (pageLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <Loader2
          className="animate-spin text-indigo-600 mb-4"
          size={40}
        />

        <p className="text-slate-500 font-medium tracking-wide">
          Syncing client data...
        </p>
      </div>
    );
  }

  // ================= NOT FOUND =================
  if (!client) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-800">
            Client Not Found
          </h2>

          <Link
            to={`/${orgSlug}/clients`}
            className="text-indigo-600 mt-4 inline-block hover:underline"
          >
            Return to list
          </Link>
        </div>
      </div>
    );
  }

  // ================= RETURN =================
  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-20">

      {/* HEADER */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30 px-4 sm:px-6 lg:px-8 py-4">

        <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center gap-4">

          {/* LEFT */}
          <div className="flex items-center gap-4 sm:gap-6">

            <Link
              to={`/${orgSlug}/clients`}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-500"
            >
              <ArrowLeft size={20} />
            </Link>

            <div>

              <div className="flex items-center gap-3 flex-wrap">

                <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                  {editMode
                    ? "Editing Profile"
                    : client.name}
                </h1>

                {!editMode && (
                  <StatusBadge
                    status={
                      client.status
                    }
                  />
                )}
              </div>

              <p className="text-slate-500 text-sm font-medium flex items-center gap-1 mt-1">
                <Building2 size={14} />

                {client.company ||
                  "Independent"}
              </p>
            </div>
          </div>

          {/* RIGHT */}
          <div className="flex items-center gap-3">

            <AnimatePresence mode="wait">

              {editMode ? (
                <motion.div
                  initial={{
                    opacity: 0,
                    x: 20,
                  }}
                  animate={{
                    opacity: 1,
                    x: 0,
                  }}
                  exit={{
                    opacity: 0,
                    x: 20,
                  }}
                  className="flex gap-2"
                >

                  <button
                    onClick={() => {
                      setEditMode(
                        false
                      );

                      setFormData(
                        client
                      );
                    }}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-all"
                  >
                    <X size={16} />

                    Cancel
                  </button>

                  <button
                    onClick={
                      handleUpdate
                    }
                    disabled={saving}
                    className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-xl text-sm font-bold shadow-lg shadow-indigo-100 transition-all disabled:opacity-50"
                  >
                    {saving ? (
                      <Loader2
                        className="animate-spin"
                        size={16}
                      />
                    ) : (
                      <Save
                        size={16}
                      />
                    )}

                    Save Changes
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  initial={{
                    opacity: 0,
                  }}
                  animate={{
                    opacity: 1,
                  }}
                  className="flex gap-2"
                >

                  <button
                    onClick={() =>
                      setEditMode(
                        true
                      )
                    }
                    className="flex items-center gap-2 border border-slate-200 hover:border-indigo-200 hover:bg-indigo-50 text-slate-700 hover:text-indigo-600 px-4 py-2 rounded-xl text-sm font-bold transition-all"
                  >
                    <Edit3 size={16} />

                    Edit Profile
                  </button>

                  <button
                    onClick={
                      handleDelete
                    }
                    disabled={
                      loadingDelete
                    }
                    className="flex items-center gap-2 border border-red-100 hover:bg-red-50 text-red-500 px-4 py-2 rounded-xl text-sm font-bold transition-all"
                  >
                    {loadingDelete ? (
                      <Loader2
                        className="animate-spin"
                        size={16}
                      />
                    ) : (
                      <Trash2
                        size={16}
                      />
                    )}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* BODY */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">

        {/* LEFT SIDE */}
        <div className="lg:col-span-2 space-y-6">

          {/* ORGANIZATION DETAILS */}
          <SectionCard
            icon={
              <Building2
                size={18}
              />
            }
            title="Organization Details"
          >

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">

              <DetailField
                label="Full Name"
                name="name"
                value={
                  client.name
                }
                editMode={
                  editMode
                }
                onChange={
                  handleChange
                }
                formData={
                  formData
                }
              />

              <DetailField
                label="Company"
                name="company"
                value={
                  client.company
                }
                editMode={
                  editMode
                }
                onChange={
                  handleChange
                }
                formData={
                  formData
                }
              />

              <DetailField
                label="Industry"
                name="industry"
                value={
                  client.industry
                }
                editMode={
                  editMode
                }
                onChange={
                  handleChange
                }
                formData={
                  formData
                }
              />

              {editMode ? (
                <SelectField
                  label="Service Needed"
                  name="serviceNeeded"
                  value={
                    formData.serviceNeeded ||
                    ""
                  }
                  options={
                    services
                  }
                  onChange={
                    handleChange
                  }
                />
              ) : (
                <DetailField
                  label="Service Needed"
                  name="serviceNeeded"
                  value={
                    client.serviceNeeded
                  }
                  editMode={
                    false
                  }
                />
              )}

              <DetailField
                label="Website"
                name="website"
                value={
                  client.website
                }
                editMode={
                  editMode
                }
                onChange={
                  handleChange
                }
                formData={
                  formData
                }
              />

              {editMode ? (
                <SelectField
                  label="Client Type"
                  name="status"
                  value={
                    formData.status ||
                    "lead"
                  }
                  options={
                    clientTypes
                  }
                  onChange={
                    handleChange
                  }
                />
              ) : (
                <div>

                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-2">
                    Client Type
                  </label>

                  <StatusBadge
                    status={
                      client.status
                    }
                  />
                </div>
              )}
            </div>

            <div className="mt-10 pt-8 border-t border-slate-100">

              <DetailField
                label="Project Description"
                name="description"
                value={
                  client.description
                }
                editMode={
                  editMode
                }
                onChange={
                  handleChange
                }
                formData={
                  formData
                }
                isTextArea
              />
            </div>
          </SectionCard>

          {/* LOCATION */}
          <SectionCard
            icon={
              <Globe size={18} />
            }
            title="Location Details"
          >

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

              <div className="md:col-span-3">

                <DetailField
                  label="Street Address"
                  name="address"
                  value={
                    client.address
                  }
                  editMode={
                    editMode
                  }
                  onChange={
                    handleChange
                  }
                  formData={
                    formData
                  }
                />
              </div>

              <DetailField
                label="City"
                name="city"
                value={
                  client.city
                }
                editMode={
                  editMode
                }
                onChange={
                  handleChange
                }
                formData={
                  formData
                }
              />

              <DetailField
                label="District"
                name="district"
                value={
                  client.district
                }
                editMode={
                  editMode
                }
                onChange={
                  handleChange
                }
                formData={
                  formData
                }
              />

              <DetailField
                label="State"
                name="state"
                value={
                  client.state
                }
                editMode={
                  editMode
                }
                onChange={
                  handleChange
                }
                formData={
                  formData
                }
              />

              <DetailField
                label="Country"
                name="country"
                value={
                  client.country
                }
                editMode={
                  editMode
                }
                onChange={
                  handleChange
                }
                formData={
                  formData
                }
              />

              <DetailField
                label="Pincode"
                name="pincode"
                value={
                  client.pincode
                }
                editMode={
                  editMode
                }
                onChange={
                  handleChange
                }
                formData={
                  formData
                }
              />
            </div>
          </SectionCard>

          {/* SALES JOURNEY */}
          <SectionCard
            icon={
              <Briefcase
                size={18}
              />
            }
            title="Sales Journey"
          >

            <div className="space-y-4">

              {/* PROGRESS */}
              <div className="mb-6">

                <div className="flex justify-between text-sm font-bold text-slate-600 mb-2">

                  <span>
                    Sales Progress
                  </span>

                  <span>
                    {client.salesStar ||
                      1}
                    /10
                  </span>
                </div>

                <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">

                  <div
                    className="h-full bg-gradient-to-r from-indigo-500 to-blue-500 transition-all duration-500"
                    style={{
                      width: `${
                        ((client.salesStar ||
                          1) /
                          10) *
                        100
                      }%`,
                    }}
                  />
                </div>
              </div>

              {/* SALES STEPS */}
              {salesJourneySteps.map(
                (step) => {

                  const active =
                    (client.salesStar ||
                      1) >=
                    step.star;

                  return (
                    <div
                      key={
                        step.star
                      }
                      className={`flex items-center justify-between p-4 rounded-2xl border transition-all ${
                        active
                          ? "bg-slate-50 border-slate-200"
                          : "bg-white border-slate-100 opacity-60"
                      }`}
                    >

                      <div className="flex items-center gap-4">

                       <div className="flex gap-0.5">
  {[...Array(step.star)].map((_, i) => (
    <span
      key={i}
      className="text-amber-400 text-lg"
    >
      ★
    </span>
  ))}
</div>
                        <div>

                          <h4 className="font-bold text-slate-800">
                            {
                              step.title
                            }
                          </h4>

                          <p className="text-xs text-slate-500">
                            Stage{" "}
                            {
                              step.star
                            }{" "}
                            of 10
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">

                        {editMode &&
                          (client.salesStar ||
                            1) +
                            1 ===
                            step.star && (
                            <button
                              onClick={() =>
                                updateSalesStage(
                                  step
                                )
                              }
                              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold"
                            >
                              Complete
                            </button>
                          )}

                        {active && (
                          <span className="text-green-600 font-bold text-sm">
                            Completed
                          </span>
                        )}
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </SectionCard>
        </div>

        {/* RIGHT SIDE */}
        <div className="space-y-6">

          {/* REVENUE CARD */}
          <div className="bg-gradient-to-br from-indigo-600 to-blue-600 rounded-3xl p-8 text-white shadow-xl shadow-indigo-100 relative overflow-hidden">

            <DollarSign
              className="absolute -right-4 -bottom-4 text-white opacity-10"
              size={140}
            />

            <h3 className="text-xs font-bold uppercase tracking-widest text-indigo-200 mb-4">
              Contract Value
            </h3>

            <div className="mb-8">

              {editMode ? (
                <input
                  type="number"
                  name="revenue"
                  value={
                    formData.revenue ||
                    ""
                  }
                  onChange={
                    handleChange
                  }
                  className="bg-white/10 border border-white/20 rounded-2xl text-white w-full outline-none px-4 py-3 text-3xl font-black placeholder:text-white/50"
                />
              ) : (
                <h2 className="text-4xl font-black">
                  ₹
                  {parseFloat(
                    client.revenue ||
                      0
                  ).toLocaleString(
                    "en-IN"
                  )}
                </h2>
              )}
            </div>

            <div className="space-y-4">

              <div className="flex justify-between items-center text-sm">

                <span className="text-indigo-200 flex items-center gap-2">
                  <Calendar
                    size={14}
                  />

                  Start
                </span>

                <span className="font-bold">
                  {client.projectStartDate ||
                    "N/A"}
                </span>
              </div>

              <div className="flex justify-between items-center text-sm">

                <span className="text-indigo-200 flex items-center gap-2">
                  <Calendar
                    size={14}
                  />

                  Due Date
                </span>

                <span className="font-bold">
                  {client.projectDueDate ||
                    "N/A"}
                </span>
              </div>
            </div>
          </div>

          {/* CONTACT CARD */}
          <SectionCard
            icon={
              <Mail size={18} />
            }
            title="Point of Contact"
          >

            <div className="space-y-6">

              <DetailField
                label="Contact Email"
                name="contactEmail"
                value={
                  client.contactEmail
                }
                editMode={
                  editMode
                }
                onChange={
                  handleChange
                }
                formData={
                  formData
                }
              />

              <DetailField
                label="Phone Number"
                name="phone"
                value={
                  client.phone
                }
                editMode={
                  editMode
                }
                onChange={
                  handleChange
                }
                formData={
                  formData
                }
              />
            </div>
          </SectionCard>

          {/* PROJECT CARD */}
          <SectionCard
            icon={
              <Briefcase
                size={18}
              />
            }
            title="Project Timeline"
          >

            <div className="space-y-6">

              <DetailField
                label="Project Start Date"
                name="projectStartDate"
                value={
                  client.projectStartDate
                }
                editMode={
                  editMode
                }
                onChange={
                  handleChange
                }
                formData={
                  formData
                }
                type="date"
              />

              <DetailField
                label="Project Due Date"
                name="projectDueDate"
                value={
                  client.projectDueDate
                }
                editMode={
                  editMode
                }
                onChange={
                  handleChange
                }
                formData={
                  formData
                }
                type="date"
              />
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
};

// ================= SECTION CARD =================
const SectionCard = ({
  icon,
  title,
  children,
}) => (
  <motion.div
    initial={{
      opacity: 0,
      y: 10,
    }}
    animate={{
      opacity: 1,
      y: 0,
    }}
    className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm"
  >
    <div className="flex items-center gap-3 mb-8 border-b border-slate-100 pb-4">

      <div className="w-10 h-10 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600">
        {icon}
      </div>

      <h2 className="text-sm font-black uppercase tracking-[0.2em] text-slate-700">
        {title}
      </h2>
    </div>

    {children}
  </motion.div>
);

// ================= DETAIL FIELD =================
const DetailField = ({
  label,
  name,
  value,
  editMode,
  onChange,
  formData,
  isTextArea = false,
  type = "text",
}) => (
  <div className="w-full">

    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-2">
      {label}
    </label>

    {editMode ? (
      isTextArea ? (
        <textarea
          name={name}
          value={
            formData?.[name] ||
            ""
          }
          onChange={onChange}
          rows={5}
          className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm font-medium focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all resize-none"
        />
      ) : (
        <input
          type={type}
          name={name}
          value={
            formData?.[name] ||
            ""
          }
          onChange={onChange}
          className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm font-medium focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all"
        />
      )
    ) : (
      <p className="text-slate-800 font-bold text-sm tracking-tight leading-relaxed break-words">
        {value || "—"}
      </p>
    )}
  </div>
);

// ================= SELECT FIELD =================
const SelectField = ({
  label,
  options,
  ...props
}) => (
  <div className="w-full">

    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-2">
      {label}
    </label>

    <select
      {...props}
      className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm font-medium focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all"
    >
      {options.map(
        (option, index) => {

          if (
            typeof option ===
            "string"
          ) {
            return (
              <option
                key={index}
                value={option}
              >
                {option}
              </option>
            );
          }

          return (
            <option
              key={
                option.value
              }
              value={
                option.value
              }
            >
              {option.label}
            </option>
          );
        }
      )}
    </select>
  </div>
);

// ================= STATUS BADGE =================
const StatusBadge = ({
  status,
}) => {

  const config = {
    lead: {
      style:
        "bg-amber-50 text-amber-600 border-amber-100",

      label: "Lead",
    },

    contact: {
      style:
        "bg-blue-50 text-blue-600 border-blue-100",

      label: "Contact",
    },

    client: {
      style:
        "bg-emerald-50 text-emerald-600 border-emerald-100",

      label: "Client",
    },
  };

  const normalized = String(
    status || ""
  )
    .trim()
    .toLowerCase();

  const current =
    config[normalized] || {
      style:
        "bg-slate-100 text-slate-600 border-slate-200",

      label: "Unknown",
    };

  return (
    <span
      className={`px-3 py-1 rounded-xl text-[10px] font-black uppercase tracking-widest border ${current.style}`}
    >
      {current.label}
    </span>
  );
};

export default ClientDetails;
